import { useQueryClient } from '@tanstack/react-query'
import {
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import * as React from 'react'
import { useCallback, useState } from 'react'
import { useOrgUsersApi } from 'src/api/db/apiOrgUsers'
import { queryKeys } from 'src/api/db/queryKeys'
import { Column } from 'src/shared/components/Semantic/all'
import { TeamMember, EditUserFormData } from './types'
import { EditUserDialog } from './components/EditUserDialog'
import { TableActions } from './components/TableActions'
import { TeamMembersTable } from './components/TeamMembersTable'
import { createTableColumns } from './components/TableColumns'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { useOrgsApi } from 'src/api/db/apiOrgs'

export function TeamSettings({ org_id }: { org_id?: string }) {
  const { t } = useLocalization()
  const queryClient = useQueryClient()
  const { orgUsers, updateUser, deleteOrgUser } = useOrgUsersApi(org_id)
  const { organization } = useOrgsApi({})
  const [isEditUserModalOpen, setIsEditUserModalOpen] = useState(false)
  const [editingUser, setEditingUser] = useState<TeamMember | null>(null)

  const [sorting, setSorting] = React.useState<SortingState>([])
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    []
  )
  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>({})
  const [rowSelection, setRowSelection] = React.useState({})

  const data = React.useMemo(
    () =>
      (orgUsers?.data?.map(({ user, user_id, created_at, role, id }) => ({
        id: user?.id ?? user_id,
        org_user_id: id,
        first_name: user?.first_name || null,
        last_name: user?.last_name || null,
        phone: user?.phone || null,
        joined: created_at,
        email: user?.email ?? null,
        role_id: role?.id,
      })) as TeamMember[]) || [],
    [orgUsers?.data]
  )

  const handleEditUser = useCallback((user: TeamMember) => {
    setEditingUser(user)
    setIsEditUserModalOpen(true)
  }, [])

  const handleSaveUser = useCallback(
    async (formData: EditUserFormData) => {
      if (!editingUser) return

      await updateUser.mutateAsync({
        id: editingUser.id,
        first_name: formData.first_name || null,
        last_name: formData.last_name || null,
        phone: formData.phone || null,
      })

      setIsEditUserModalOpen(false)
      setEditingUser(null)
      queryClient.invalidateQueries({ queryKey: [queryKeys.org_users] })
    },
    [editingUser, updateUser, queryClient]
  )

  const handleRemoveUser = useCallback(
    async (orgUserId: number) => {
      await deleteOrgUser.mutateAsync({ id: orgUserId })
    },
    [deleteOrgUser]
  )

  const columns = React.useMemo(
    () =>
      createTableColumns({
        onEditUser: handleEditUser,
        onRemoveUser: handleRemoveUser,
        t,
      }),
    [handleEditUser, handleRemoveUser, t]
  )

  const table = useReactTable({
    data,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
    },
  })

  return (
    <Column className="w-full">
      <TeamMembersTable
        table={table}
        onEditUser={handleEditUser}
        onRemoveUser={handleRemoveUser}
      />

      <TableActions
        organizationId={org_id || organization?.data?.id!}
        selectedRowCount={table.getFilteredSelectedRowModel().rows.length}
        totalRowCount={table.getFilteredRowModel().rows.length}
      />

      <EditUserDialog
        isOpen={isEditUserModalOpen}
        onOpenChange={setIsEditUserModalOpen}
        editingUser={editingUser}
        onSave={handleSaveUser}
        isLoading={updateUser.isPending}
      />
    </Column>
  )
}
